(() => {
  'use strict';

  const roots = document.querySelectorAll('[data-amanis-reminders]');
  if (!roots.length) return;

  const qsa = (root, selector) => Array.from(root.querySelectorAll(selector));
  const qs = (root, selector) => root.querySelector(selector);

  function compactNumber(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return '';
    if (n < 1000) return String(n);
    if (n < 1000000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1).replace('.0','')}K`;
    return `${(n / 1000000).toFixed(n >= 10000000 ? 0 : 1).replace('.0','')}M`;
  }

  function getGuestId() {
    const key = 'amani_reminders_guest_v1';
    try {
      let id = localStorage.getItem(key);
      if (!id) {
        id = (window.crypto && crypto.randomUUID) ? crypto.randomUUID() :
          'g-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);
        localStorage.setItem(key, id);
      }
      return id;
    } catch (_) {
      return '';
    }
  }

  function readLocalFlag(kind, id) {
    try { return localStorage.getItem(`amani_reminders_${kind}_${id}`) === '1'; }
    catch (_) { return false; }
  }

  function writeLocalFlag(kind, id, value) {
    try {
      const key = `amani_reminders_${kind}_${id}`;
      value ? localStorage.setItem(key, '1') : localStorage.removeItem(key);
    } catch (_) {}
  }

  function toast(root, message) {
    const el = qs(root, '[data-reminders-toast]');
    if (!el) return;
    el.textContent = message;
    el.classList.add('is-visible');
    clearTimeout(el.__timer);
    el.__timer = setTimeout(() => el.classList.remove('is-visible'), 2200);
  }

  function sortTopicLinks(root) {
    qsa(root, '[data-topic-group]').forEach(group => {
      const items = Array.from(group.children).filter(el => el.hasAttribute && el.hasAttribute('data-sort-order'));
      items
        .sort((a, b) => Number(a.dataset.sortOrder || 0) - Number(b.dataset.sortOrder || 0))
        .forEach(item => group.appendChild(item));
    });
  }

  function initMore(root) {
    const toggle = qs(root, '[data-more-toggle]');
    const panel = qs(root, '[data-more-panel]');
    if (!toggle || !panel) return;

    const close = () => {
      panel.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
    };
    const open = () => {
      panel.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
    };

    toggle.addEventListener('click', () => {
      toggle.getAttribute('aria-expanded') === 'true' ? close() : open();
    });
    document.addEventListener('click', event => {
      if (!panel.classList.contains('is-open')) return;
      if (!panel.contains(event.target) && !toggle.contains(event.target)) close();
    });
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape' && panel.classList.contains('is-open')) {
        close();
        toggle.focus();
      }
    });
  }

  function initSearch(root) {
    const input = qs(root, '[data-reminders-search]');
    const status = qs(root, '[data-reminders-search-status]');
    const cards = qsa(root, '[data-reminder-card]');
    if (!input || !cards.length) return;

    const update = () => {
      const term = input.value.trim().toLocaleLowerCase();
      let visible = 0;
      cards.forEach(card => {
        const haystack = (card.dataset.search || '').toLocaleLowerCase();
        const show = !term || haystack.includes(term);
        card.hidden = !show;
        if (show) visible++;
      });
      if (status) status.textContent = term ? `${visible} reminder${visible === 1 ? '' : 's'} shown on this page.` : '';
    };
    input.addEventListener('input', update);
  }

  function shareUrl(url, title) {
    if (navigator.share) return navigator.share({ title, url });
    if (navigator.clipboard && navigator.clipboard.writeText) return navigator.clipboard.writeText(url);
    return Promise.reject(new Error('Clipboard unavailable'));
  }

  function initSharing(root) {
    root.addEventListener('click', async event => {
      const share = event.target.closest('[data-reminder-share]');
      const copy = event.target.closest('[data-reminder-copy]');
      const save = event.target.closest('[data-reminder-image-save]');
      if (!share && !copy && !save) return;

      const host = event.target.closest('[data-reminder-id]') || root;
      const url = host.dataset.reminderUrl || window.location.href;
      const title = host.dataset.reminderTitle || document.title;
      const image = host.dataset.reminderImage || '';

      try {
        if (share) {
          await shareUrl(url, title);
          toast(root, navigator.share ? 'Shared.' : 'Link copied.');
        } else if (copy) {
          if (!navigator.clipboard || !navigator.clipboard.writeText) throw new Error('Clipboard unavailable');
          await navigator.clipboard.writeText(url);
          toast(root, 'Link copied.');
        } else if (save && image) {
          const response = await fetch(image, { mode: 'cors' });
          if (!response.ok) throw new Error('Image request failed');
          const blob = await response.blob();
          const objectUrl = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = objectUrl;
          a.download = save.dataset.filename || 'amanis-reminder';
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
          toast(root, 'Image saved.');
        }
      } catch (_) {
        if (copy || share) {
          try {
            if (!navigator.clipboard || !navigator.clipboard.writeText) throw new Error('Clipboard unavailable');
            await navigator.clipboard.writeText(url);
            toast(root, 'Link copied.');
          } catch (__) {
            window.prompt('Copy this link:', url);
          }
        } else if (save && image) {
          window.open(image, '_blank', 'noopener');
        }
      }
    });
  }

  async function requestJSON(url, options = {}) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
      ...options
    });
    if (!response.ok) throw new Error(`Request failed: ${response.status}`);
    return response.json();
  }

  function initEngagement(root) {
    const endpoint = (root.dataset.engagementEndpoint || '').replace(/\/$/, '');
    const items = qsa(root, '[data-reminder-id]');
    const uniqueIds = [...new Set(items.map(item => item.dataset.reminderId).filter(Boolean))];
    if (!uniqueIds.length) return;

    const updateEverywhere = (id, data, showCounts = true) => {
      qsa(root, `[data-reminder-id="${CSS.escape(id)}"]`).forEach(item => {
        const groups = qsa(item, '[data-engagement]');
        groups.forEach(group => group.dataset.ready = 'true');

        qsa(item, '[data-reminder-heart]').forEach(heart => {
          if (typeof data.hearted === 'boolean') {
            heart.removeAttribute('aria-disabled');
            heart.setAttribute('aria-pressed', data.hearted ? 'true' : 'false');
            const icon = qs(heart, '[data-heart-icon]');
            if (icon) icon.textContent = data.hearted ? '♥' : '♡';
          }
        });

        qsa(item, '[data-heart-count]').forEach(count => {
          if (showCounts && Number.isFinite(Number(data.heart_count))) {
            count.textContent = compactNumber(data.heart_count);
            count.hidden = false;
          } else {
            count.hidden = true;
          }
        });

        qsa(item, '[data-reminder-ameen]').forEach(ameen => {
          if (typeof data.said_ameen === 'boolean') {
            ameen.removeAttribute('aria-disabled');
            ameen.setAttribute('aria-pressed', data.said_ameen ? 'true' : 'false');
            ameen.dataset.said = data.said_ameen ? 'true' : 'false';
            const label = qs(ameen, '[data-ameen-label]');
            if (label) label.textContent = data.said_ameen ? '✓ Ameen' : 'Ameen';
          }
        });

        qsa(item, '[data-ameen-count]').forEach(count => {
          if (showCounts && Number.isFinite(Number(data.ameen_count))) {
            count.textContent = compactNumber(data.ameen_count);
            count.hidden = false;
          } else {
            count.hidden = true;
          }
        });
      });
    };

    /* Safe staging/standalone fallback: real per-device state, never fake global counts. */
    if (!endpoint) {
      uniqueIds.forEach(id => {
        updateEverywhere(id, {
          hearted: readLocalFlag('heart', id),
          said_ameen: readLocalFlag('ameen', id)
        }, false);
      });

      root.addEventListener('click', event => {
        const heart = event.target.closest('[data-reminder-heart]');
        const ameen = event.target.closest('[data-reminder-ameen]');
        if (!heart && !ameen) return;
        const item = event.target.closest('[data-reminder-id]');
        if (!item) return;
        const id = item.dataset.reminderId;

        if (heart) {
          const desired = heart.getAttribute('aria-pressed') !== 'true';
          writeLocalFlag('heart', id, desired);
          updateEverywhere(id, { hearted: desired, said_ameen: readLocalFlag('ameen', id) }, false);
          toast(root, desired ? 'Saved to this device.' : 'Removed from saved.');
          return;
        }

        if (ameen) {
          if (readLocalFlag('ameen', id)) {
            toast(root, 'You already said Ameen.');
            return;
          }
          writeLocalFlag('ameen', id, true);
          updateEverywhere(id, { hearted: readLocalFlag('heart', id), said_ameen: true }, false);
          toast(root, 'May Allah accept it from you. Ameen.');
        }
      });
      return;
    }

    const guestId = getGuestId();
    const customerId = document.body.dataset.customerId || '';
    const actor = customerId ? { actor_type: 'customer', customer_id: customerId } : { actor_type: 'guest', guest_id: guestId };

    Promise.all([
      requestJSON(`${endpoint}/api/stats?ids=${encodeURIComponent(uniqueIds.join(','))}`),
      requestJSON(`${endpoint}/api/state`, {
        method: 'POST',
        body: JSON.stringify({ reminder_ids: uniqueIds, ...actor })
      })
    ]).then(([stats, state]) => {
      const statsMap = stats.reminders || stats.data || {};
      const stateMap = state.reminders || state.data || {};
      uniqueIds.forEach(id => updateEverywhere(id, { ...(statsMap[id] || {}), ...(stateMap[id] || {}) }, true));
    }).catch(() => {
      qsa(root, '[data-engagement]').forEach(group => group.dataset.ready = 'false');
    });

    root.addEventListener('click', async event => {
      const heart = event.target.closest('[data-reminder-heart]');
      const ameen = event.target.closest('[data-reminder-ameen]');
      if (!heart && !ameen) return;
      const item = event.target.closest('[data-reminder-id]');
      if (!item) return;

      const id = item.dataset.reminderId;
      const button = heart || ameen;
      if (button.dataset.busy === 'true') return;
      button.dataset.busy = 'true';

      try {
        if (heart) {
          const desired = heart.getAttribute('aria-pressed') !== 'true';
          const data = await requestJSON(`${endpoint}/api/heart`, {
            method: 'POST',
            body: JSON.stringify({ reminder_id: id, hearted: desired, ...actor })
          });
          updateEverywhere(id, data, true);
        } else if (ameen) {
          if (ameen.dataset.said === 'true') {
            toast(root, 'You already said Ameen.');
            return;
          }
          const data = await requestJSON(`${endpoint}/api/ameen`, {
            method: 'POST',
            body: JSON.stringify({ reminder_id: id, ...actor })
          });
          updateEverywhere(id, data, true);
          toast(root, 'May Allah accept it from you. Ameen.');
        }
      } catch (_) {
        toast(root, 'That could not be saved just now. Please try again.');
      } finally {
        button.dataset.busy = 'false';
      }
    });
  }

  roots.forEach(root => {
    sortTopicLinks(root);
    initMore(root);
    initSearch(root);
    initSharing(root);
    initEngagement(root);
  });
})();